import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Eye, X, Mail } from 'lucide-react';
import { sapi } from '../../lib/api';
import { Spinner, useToast, Toast, ConfirmModal } from '../../components/UI';

interface EmailBody { id: string; name: string; subject: string; body: string; createdAt: string; }

const PH = ['{{company_name}}','{{address}}','{{manager_name}}','{{job_title}}','{{admirables}}','{{your_name}}'];

const DEFAULT_BODY = `Dear {{manager_name}},

Please find attached my CV and cover letter in application for the {{job_title}} position. I am very interested in joining {{company_name}} and I particularly admire {{admirables}}.

I would welcome the opportunity to discuss this further.

Kind regards,
{{your_name}}`;

export default function EmailBodiesPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [items, setItems] = useState<EmailBody[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'add' | EmailBody | null>(null);
  const [preview, setPreview] = useState<EmailBody | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailBody | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', subject: 'Job Application — {{company_name}}', body: DEFAULT_BODY });
  const [bodyRef, setBodyRef] = useState<HTMLTextAreaElement | null>(null);

  useEffect(() => { sapi.getEmailBodies().then(setItems).catch(() => setItems([])).finally(() => setLoading(false)); }, []);

  const openAdd = () => { setForm({ name: '', subject: 'Job Application — {{company_name}}', body: DEFAULT_BODY }); setModal('add'); };
  const openEdit = (eb: EmailBody) => { setForm({ name: eb.name, subject: eb.subject, body: eb.body }); setModal(eb); };

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) { show('Name and body required', 'error'); return; }
    setSaving(true);
    try {
      if (modal === 'add') {
        const created = await sapi.createEmailBody(form.name, form.subject, form.body);
        setItems(p => [created, ...p]); show(`"${form.name}" saved`, 'success');
      } else if (modal && typeof modal === 'object') {
        const updated = await sapi.updateEmailBody(modal.id, form.name, form.subject, form.body);
        setItems(p => p.map(i => i.id === updated.id ? updated : i)); show('Updated', 'success');
      }
      setModal(null);
    } catch (e: any) { show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    await sapi.deleteEmailBody(deleteTarget.id).catch(() => null);
    setItems(p => p.filter(i => i.id !== deleteTarget.id));
    show('Deleted', 'info'); setDeleteTarget(null);
  };

  const insertPH = (ph: string) => {
    if (!bodyRef) return;
    const s = bodyRef.selectionStart, e = bodyRef.selectionEnd;
    const val = form.body.slice(0, s) + ph + form.body.slice(e);
    setForm(f => ({ ...f, body: val }));
    setTimeout(() => { bodyRef.focus(); bodyRef.setSelectionRange(s + ph.length, s + ph.length); }, 0);
  };

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <div className="page-header">
        <div><h1 className="page-title">Email Bodies</h1><p className="page-sub">{items.length} template{items.length !== 1 ? 's' : ''}</p></div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> New Template</button>
      </div>

      {loading ? <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><Spinner size={28} /></div>
      : items.length === 0 ? (
        <div className="card"><div className="empty-state"><Mail size={32} /><h3>No email body templates</h3><p>Create a reusable email body for job applications.</p><button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> New Template</button></div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {items.map(eb => (
            <div key={eb.id} className="card" style={{ borderLeft: '3px solid var(--accent2)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{eb.name}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPreview(eb)}><Eye size={12} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(eb)}><Edit2 size={12} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(eb)}><Trash2 size={12} /></button>
                </div>
              </div>
              {eb.subject && <div style={{ fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>Subject: {eb.subject}</div>}
              <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>{eb.body}</div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal modal-wide">
            <div className="modal-header"><h3>{modal === 'add' ? 'New Email Body Template' : `Edit — ${(modal as EmailBody).name}`}</h3><button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}><X size={14} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label>Template Name *</label><input placeholder="e.g. Software Dev Application" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus /></div>
              <div className="form-group"><label>Subject Line</label><input placeholder="Job Application — {{company_name}}" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Email Body *</label>
                <textarea ref={setBodyRef} value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} style={{ minHeight: 220, fontFamily: 'DM Sans, sans-serif', fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>Insert:</span>
                {PH.map(ph => <button key={ph} className="btn btn-ghost btn-sm" style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }} onClick={() => insertPH(ph)}>{ph}</button>)}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : null}{modal === 'add' ? 'Save Template' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPreview(null)}>
          <div className="modal modal-wide">
            <div className="modal-header"><h3>{preview.name}</h3><button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}><X size={14} /></button></div>
            <div className="modal-body">
              {preview.subject && <div style={{ marginBottom: 12 }}><div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Subject</div><div style={{ fontSize: 13 }}>{preview.subject}</div></div>}
              <div className="preview-box" style={{ whiteSpace: 'pre-wrap', minHeight: 160 }}>{preview.body}</div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && <ConfirmModal title="Delete Template" message={<>Delete <strong>{deleteTarget.name}</strong>?</>} onConfirm={del} onCancel={() => setDeleteTarget(null)} danger />}
    </div>
  );
}
